/**
 * Type definitions for the Document Library feature module (MYD-282).
 *
 * Mirrors the backend models from MYD-275, MYD-279, and MYD-281.
 * Where the backend `populate()`s a ref, the field is typed as
 * `string | <PopulatedShape>` to handle both shapes gracefully.
 */

export interface UserSummary {
	_id: string
	firstName?: string
	lastName?: string
	email?: string
	/** Profile photo URL. Projected by the list/get populate; may be unset. */
	image?: string
}

export type PreviewStatus = 'pending' | 'ready' | 'failed'

/**
 * The surface a document belongs to. Set on create and immutable after.
 * - `project`   → `project` FK required, others absent
 * - `portfolio` → `portfolio` FK required, others absent
 * - `location`  → `location` FK required, others absent (MYD-305+)
 * - `global`    → no FK; cross-workspace admin scope (MYD-273 epic Phase 2 decision)
 */
export type LibraryDocumentScope =
	| 'project'
	| 'portfolio'
	| 'location'
	| 'global'

/**
 * Discriminated-union shape passed to scope-aware thunks, services, and
 * the list component. Mirrors the backend's query/body contract:
 *   - project    → `?scope=project&project=<id>`
 *   - portfolio  → `?scope=portfolio&portfolio=<id>`
 *   - location   → `?scope=location&location=<id>`
 *   - global     → `?scope=global`
 *   - my-library → `?scope=my-library` (cross-scope rollup of every
 *                  doc the caller has read access to — owner OR
 *                  partner across projects + portfolios + locations,
 *                  plus global when admin. The `userId` field is for
 *                  client-side caching identity only; the backend uses
 *                  `req.user._id`).
 */
export type LibraryScopeArgs =
	| { scope: 'project'; projectId: string }
	| { scope: 'portfolio'; portfolioId: string }
	| { scope: 'location'; locationId: string }
	| { scope: 'global' }
	| { scope: 'my-library'; userId: string }

/**
 * Stable string key for `initializedScopes` and any per-scope memoization.
 * `${scope}:${scopeId}` for the FK-bearing variants; `global` alone for
 * admin; `my-library:${userId}` for the cross-scope rollup.
 */
export const scopeKey = (args: LibraryScopeArgs): string => {
	switch (args.scope) {
		case 'project':
			return `project:${args.projectId}`
		case 'portfolio':
			return `portfolio:${args.portfolioId}`
		case 'location':
			return `location:${args.locationId}`
		case 'global':
			return 'global'
		case 'my-library':
			return `my-library:${args.userId}`
	}
}

export interface LibraryDocumentVersion {
	_id: string
	document: string
	versionNumber: number
	isCurrent: boolean
	originalFilename: string
	storageKey: string
	storageBucket: string
	mimeType?: string
	byteSize?: number
	pageCount: number | null
	previewPdfKey: string | null
	thumbnailKey: string | null
	previewStatus: PreviewStatus
	previewError: string | null
	/**
	 * Presigned URL embedded by the list/detail endpoints when the
	 * version has a `thumbnailKey`. Transient — backend re-signs on each
	 * request (~10 min TTL). Absent for failed-preview versions.
	 */
	thumbnailUrl?: string | null
	fileTrueCreatedAt: string
	uploadedAt: string
	uploadedBy: string | UserSummary
	changeNote: string | null
	isDeleted: boolean
	createdAt: string
	updatedAt: string
}

export interface LibraryDocument {
	_id: string
	owner: string | UserSummary
	scope: LibraryDocumentScope
	/** Present when `scope === 'project'`; null/absent otherwise. */
	project: string | null
	/** Present when `scope === 'portfolio'`; null/absent otherwise. */
	portfolio?: string | null
	/** Present when `scope === 'location'`; null/absent otherwise. */
	location?: string | null
	libraryName: string
	/**
	 * Returned populated by the backend on create/list/get-one. Stored
	 * as the populated object in the slice; if a caller passes only
	 * the id (rare), helpers default-fill from `versionsByDocument`.
	 */
	currentVersion: LibraryDocumentVersion | string | null
	isActive: boolean
	isDeleted: boolean
	createdAt: string
	updatedAt: string
}

export interface DocumentShareAccessLogEntry {
	accessedAt: string
	ip: string | null
	userAgent: string | null
}

export interface DocumentShareLink {
	_id: string
	token: string
	document: string
	version:
		| string
		| Pick<LibraryDocumentVersion, '_id' | 'versionNumber' | 'originalFilename'>
	createdBy: string | UserSummary
	expiresAt: string
	revokedAt: string | null
	accessLog: DocumentShareAccessLogEntry[]
	isDeleted: boolean
	createdAt: string
	updatedAt: string
}

export interface CreateShareLinkResponse {
	token: string
	url: string
	expiresAt: string
	document: string
	version: string
	emailedTo: string | null
}

export interface RevokeShareLinkResponse {
	token: string
	revokedAt: string
}

/**
 * Backend response from `GET /api/library-documents/:id/inline-url`.
 * Drives the preview dialog — `family` decides which renderer to use
 * (PDF / image / video / failure-state). Presigned URLs expire per
 * `expiresInSec`; the dialog refetches if it stays open longer.
 */
export interface InlineUrlsResponse {
	documentId: string
	versionId: string
	family: 'pdf' | 'image' | 'video' | 'office' | 'other'
	mimeType?: string
	originalFilename: string
	pageCount: number | null
	previewStatus: PreviewStatus
	previewError: string | null
	previewUrl: string | null
	thumbnailUrl: string | null
	expiresInSec: number
}

/** Shape stored inside the Redux slice. */
export interface DocumentLibraryState {
	entities: Record<string, LibraryDocument>
	ids: string[]
	versionsByDocument: Record<string, LibraryDocumentVersion[]>
	sharesByDocument: Record<string, DocumentShareLink[]>
	/**
	 * Cached inline URLs per document. `fetchedAt` is a Date.now() epoch
	 * the dialog uses to decide whether to refetch (URLs expire in
	 * ~10 minutes).
	 */
	inlineUrlsByDocument: Record<
		string,
		{ response: InlineUrlsResponse; fetchedAt: number }
	>
	/** Per-file upload progress 0-100. Keyed by a caller-supplied progressKey. */
	uploadProgress: Record<string, number>
	/**
	 * Scope keys (from `scopeKey`) we've successfully fetched at least
	 * once. Grows monotonically; the list component reads it to decide
	 * whether to show a spinner vs. an empty state. Resets to `[]` on
	 * a hard reload (sessionStorage isn't used).
	 */
	initializedScopes: string[]
	isLoading: boolean
	error: string | null
}

/**
 * True when `doc` belongs to the given scope. Use this anywhere the
 * slice's flat `entities` map needs to be narrowed to one scope —
 * e.g. the list component's row source and the upload dialog's
 * collision-detection set.
 *
 * `my-library` is special: it's a cross-scope view, so this function
 * returns `false` for that variant. The slice reducer and the list's
 * `docs` memo special-case `my-library` directly (the access predicate
 * is server-side; the client can't replicate it without re-running the
 * same project/portfolio access checks).
 */
/** Normalise a scope FK that may arrive as a bare ObjectId string or a
 *  populated `{ _id, ... }` object (the list/create endpoints both
 *  populate via `.populate('project', 'projectName')` etc., so the FK on
 *  every doc the client sees is an object, not a string). */
const fkId = (fk: unknown): string | null => {
	if (!fk) return null
	if (typeof fk === 'string') return fk
	const id = (fk as { _id?: unknown })._id
	return id ? String(id) : null
}

export const matchesScope = (
	doc: LibraryDocument | undefined,
	args: LibraryScopeArgs,
): boolean => {
	if (!doc) return false
	if (args.scope === 'my-library') return false
	const docScope = doc.scope || 'project'
	if (docScope !== args.scope) return false
	switch (args.scope) {
		case 'project':
			return fkId(doc.project) === args.projectId
		case 'portfolio':
			return fkId(doc.portfolio) === args.portfolioId
		case 'location':
			return fkId(doc.location) === args.locationId
		case 'global':
			return true
	}
}

/** Helper: pull the current-version object regardless of whether it was populated. */
export const getCurrentVersion = (
	doc: LibraryDocument | undefined,
): LibraryDocumentVersion | null => {
	if (!doc || !doc.currentVersion) return null
	if (typeof doc.currentVersion === 'string') return null
	return doc.currentVersion
}
